import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  TwoToneTitle,
  fitTitle,
  formatPageLabel,
  hasNextSlide,
  shouldShowCounter,
} from '../_shared';
import { cn } from '@/lib/utils';

// "Night Deck" - dark bg with subtle grid; bold sans display; purple
// accent for cover title leads + SWIPE pill at the foot of every
// non-final slide.

const SURFACE_BG = '#0E1015';
// #6E60E6, not #7A6BFF. The concept-name pill puts WHITE 14px text on this
// colour, and the original gave 3.91:1 — under the 4.5:1 WCAG AA needs for
// normal-weight text at that size. Darkening to #6E60E6 takes it to 4.69:1.
//
// Kept as ONE accent rather than splitting a separate pill colour: the two
// places this is used as TEXT (the cover headline and the thumbnail title) are
// both extrabold at 20px or more, so they are "large text" and held to 3:1 —
// 4.06:1 against the #0E1015 surface, which still passes. A second near
// identical purple would read as a mistake in the palette.
const PURPLE = '#6E60E6';
const WHITE = '#FFFFFF';

const GRID_BG: React.CSSProperties = {
  backgroundColor: SURFACE_BG,
  backgroundImage: `
    linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)
  `,
  backgroundSize: '42px 42px',
};

const GRID_BG_THUMB: React.CSSProperties = {
  backgroundColor: SURFACE_BG,
  backgroundImage: GRID_BG.backgroundImage,
  backgroundSize: '16px 16px',
};

function TopBar({
  leftLabel,
  rightLabel,
}: {
  leftLabel?: string;
  rightLabel?: string;
}) {
  return (
    <div className='flex items-center gap-4 px-10 pt-8 md:px-14 md:pt-10'>
      {leftLabel && (
        <span
          className={cn(
            'shrink-0 font-medium tracking-[0.18em] text-white/90',
            TYPE.topBar
          )}
        >
          <SlideText text={leftLabel} />
        </span>
      )}
      <div
        className='flex-1 border-t-2 border-dotted'
        style={{ borderColor: 'rgba(255,255,255,0.35)' }}
        aria-hidden='true'
      />
      {rightLabel && (
        <span className={cn('shrink-0 font-bold text-white', TYPE.page)}>
          {rightLabel}
        </span>
      )}
    </div>
  );
}

function SwipePill({ leftLabel }: { leftLabel?: string }) {
  return (
    <div
      // pointer-events-none so the pill never intercepts the carousel's
      // tap-zone clicks (vertical nav uses the bottom 20% of the card).
      className='pointer-events-none mx-10 mb-8 flex h-12 items-center justify-between rounded-full pl-5 pr-1 md:mx-14 md:mb-10 md:h-14'
      style={{ backgroundColor: PURPLE }}
      aria-hidden='true'
    >
      <span className={cn('truncate pr-4 font-medium text-white', TYPE.topBar)}>
        <SlideText text={leftLabel ?? ''} />
      </span>
      <span
        className={cn(
          'flex h-10 shrink-0 items-center rounded-full bg-white px-5 font-extrabold tracking-[0.18em] text-black md:h-12 md:px-7',
          TYPE.topBar
        )}
      >
        SWIPE
      </span>
    </div>
  );
}

function CoverLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  return (
    <div className='relative flex h-full w-full flex-col' style={GRID_BG}>
      <TopBar leftLabel={conceptName} />

      <div
        className={cn(
          'flex flex-1 flex-col justify-center gap-1',
          PADDING.slideX
        )}
      >
        <TwoToneTitle
          title={card.title}
          as='h1'
          tones={[PURPLE, WHITE]}
          className={cn(
            'text-balance font-extrabold uppercase tracking-tight',
            fitTitle(card.title),
            LEADING.display
          )}
        />
      </div>

      {hasNextSlide(slideIndex, totalSlides) && (
        <SwipePill leftLabel={conceptName} />
      )}
    </div>
  );
}

function ContentLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const counter = showCounter
    ? formatPageLabel(slideIndex, totalSlides)
    : undefined;

  return (
    <div className='relative flex h-full w-full flex-col' style={GRID_BG}>
      <TopBar rightLabel={counter} />

      <div
        className={cn(
          'flex flex-1 flex-col justify-center gap-8',
          PADDING.slideX
        )}
      >
        <h2
          className={cn(
            'text-balance font-extrabold uppercase tracking-tight text-white',
            fitTitle(card.title, 'body'),
            LEADING.display
          )}
        >
          <SlideText text={card.title} />
        </h2>

        {card.body && (
          <BodyContent
            body={card.body}
            visual={card.visual}
            className='max-w-md text-white'
          />
        )}
      </div>

      {hasNextSlide(slideIndex, totalSlides) && (
        <SwipePill leftLabel={conceptName} />
      )}
    </div>
  );
}

function ThumbnailLayout({ card }: SlideLayoutProps) {
  return (
    <div
      className='relative flex h-full w-full flex-col p-5'
      style={GRID_BG_THUMB}
    >
      <div
        className='mb-3 border-t-2 border-dotted'
        style={{ borderColor: 'rgba(255,255,255,0.35)' }}
        aria-hidden='true'
      />

      <div className='flex flex-1 flex-col justify-center'>
        <TwoToneTitle
          title={card.title}
          as='h3'
          tones={[PURPLE, WHITE]}
          className={cn(
            'line-clamp-2 font-extrabold uppercase tracking-tight',
            fitTitle(card.title, 'thumb'),
            LEADING.display
          )}
        />
      </div>

      <div
        className='mt-3 flex h-6 items-center justify-end rounded-full pr-0.5'
        style={{ backgroundColor: PURPLE }}
        aria-hidden='true'
      >
        <span className='flex h-5 items-center rounded-full bg-white px-2 text-xs font-extrabold tracking-[0.15em] text-black'>
          SWIPE
        </span>
      </div>
    </div>
  );
}

export const Template05: HighlightTemplate = {
  id: 'template-05',
  name: 'Night Deck',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
